import styled, { css } from 'styled-components';

import * as MUI from '@mui/material';
import * as MUILabs from '@mui/lab';
import { Theme } from '../Theme';

export const ButtonGroup = styled.div`
	display: inline-flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin: ${(props) => {
		if (props.alignLeft) {
			return '0 auto 0 0';
		} else if (props.alignRight) {
			return '0 0 0 auto';
		} else if (props.alignCenter) {
			return '0 auto';
		}
	}};
	float: ${(props) => {
		if (props.alignLeft) {
			return 'left';
		} else if (props.alignRight) {
			return 'right';
		}
	}};
	justify-content: ${(props) => {
		if (props.alignLeft) {
			return 'start';
		} else if (props.alignRight) {
			return 'end';
		} else if (props.alignCenter) {
			return 'center';
		}
	}};
	align-self: ${(props) => {
		if (props.alignTop) {
			return 'flex-start';
		} else if (props.alignBottom) {
			return 'flex-end';
		} else if (props.alignCenter) {
			return 'center';
		}
	}};
`;

export const CommonButtonStyle = css`
	gap: 4px !important;
	height: 2rem !important;
	width: ${(props) => {
		if (props.fit) {
			return 'min-content';
		} else if (!props.fullWidth) {
			return 'max-content';
		}
	}} !important;
	min-width: ${(props) => {
		if (props.fit) {
			return 'min-content';
		} else if (!props.fullWidth) {
			return '8rem';
		}
	}} !important;
	max-width: ${(props) => {
		if (props.fit) {
			return 'min-content';
		} else if (!props.fullWidth) {
			return 'max-content';
		}
	}} !important;
	margin: 0rem !important;
	padding: 0.75rem !important;
	color: ${(props) => {
		if (props.loading) {
			return Theme.transparent;
		} else if (!props.outline && props.color) {
			return Theme.white;
		}
	}} !important;
	font-family: Arial !important;
	font-weight: 500 !important;
	font-size: 12px !important;
	line-height: 14px !important;
	letter-spacing: 0.02em !important;
	text-transform: none !important;
	border-width: 1.5px !important;
	border-radius: 4px !important;
	align-self: ${(props) => {
		if (props.alignLeft) {
			return 'left';
		} else if (props.alignRight) {
			return 'right';
		} else if (props.alignCenter) {
			return 'center';
		}
	}} !important;
	float: ${(props) => {
		if (props.alignLeft) {
			return 'left';
		} else if (props.alignRight) {
			return 'right';
		} else if (props.alignCenter) {
			return null;
		}
	}} !important;
	justify-content: ${(props) => {
		if (props.textLeft) {
			return 'left';
		} else if (props.textRight) {
			return 'right';
		} else if (props.textCenter) {
			return 'center';
		}
	}} !important;
	cursor: ${(props) => {
		if (props.disabled || props.loading) {
			return 'not-allowed';
		}
		return 'pointer';
	}} !important;
	disabled: ${(props) => props.disabled} !important;
	opacity: ${(props) => (props.disabled ? '0.4' : null)} !important;

	@media only screen and (max-width: 720px) {
		min-width: max-content !important;
	}
`;

export const CommonButtonAttributes = (props) => {
	const attributes = {
		variant: props.variant || 'contained',
		color: props.color || 'secondary',
		disabled: props.disabled || false,
	};

	if (props.primary || props.secondary || props.success || props.error) {
		if (props.primary) {
			attributes.color = 'primary';
		} else if (props.secondary) {
			attributes.color = 'secondary';
		} else if (props.success) {
			attributes.color = 'success';
		} else if (props.error) {
			attributes.color = 'error';
		}
	}

	if (props.outline) {
		attributes.variant = 'outlined';
	}

	if (props.loading) {
		attributes.disabled = true;
	}

	return attributes;
};

const AdditionalButtonProps = [
	'primary',
	'secondary',
	'success',
	'error',
	'fullWidth',
	'alignLeft',
	'alignCenter',
	'alignRight',
	'textLeft',
	'textRight',
	'textCenter',
	'loading',
	'outline',
	'disabled',
];

// .withConfig({
//     shouldForwardProp: (prop) => !(AdditionalButtonProps.includes(prop)),
// })
export const Button = styled(MUI.Button).attrs((props) => ({
	...CommonButtonAttributes(props),
}))`
	${CommonButtonStyle}
`;

export const ButtonWithLoader = styled(MUILabs.LoadingButton).attrs((props) => ({
	...CommonButtonAttributes(props),
	loading: props.loading || false,
}))`
	${CommonButtonStyle}
`;

export const CircularButton = styled(Button)`
	height: 2rem !important;
	width: 2rem !important;
	min-width: 2rem !important;
	text-align: center !important;
	border-width: 0 !important;
	border-radius: 50% !important;
	box-shadow: unset !important;
`;

export const CircularButtonWithLoader = styled(ButtonWithLoader)`
	height: 2rem !important;
	width: 2rem !important;
	min-width: 2rem !important;
	text-align: center !important;
	border-width: 0 !important;
	border-radius: 50% !important;
	box-shadow: unset !important;
`;

export const SquareButton = styled(Button)`
	height: 2rem !important;
	width: 2rem !important;
	min-width: 2rem !important;
	max-width: 2rem !important;
	text-align: center !important;
`;

export const SquareButtonWithLoader = styled(ButtonWithLoader)`
	height: 2rem !important;
	width: 2rem !important;
	min-width: 2rem !important;
	max-width: 2rem !important;
	text-align: center !important;
`;

export const OutlinedButton = styled(Button)`
    color: #00A67E !important;
    background-color: #FAFAFA !important;
    border: 1px solid #EAEAEA !important;
    text-transform: none !important;
    &:hover {
        border-color: #00A67E !important;
        background-color: rgba(0, 166, 126, 0.04) !important;
    }
	font-weight: 600 !important;
    letter-spacing: 0px !important;
	marginTop:10px;
`;

export const FilledButton = styled(Button)`
	color: #FFFFFF !important;
	background-color: #00AD6F !important;
	border: 1px solid #00A67E !important;
	text-transform: none !important;
	&:hover {
		background-color: rgba(0, 173, 111, 0.8) !important;
		border-color: #00A67E !important;
	}
	font-weight: 600 !important;
	letter-spacing: 0px !important;
	marginTop:10px;
`;

export const OutlinedWarningButton = styled(Button)`
	color: #FF7004 !important;
	background-color:  #FAFAFA !important;
	text-transform: none !important;
	font-weight: 600 !important;
	letter-spacing: 0px !important;
	marginTop:10px;
`;

export const OutlinedErrorButton = styled(Button)`
	color: red !important;
	background-color:  #FAFAFA !important;
	text-transform: none !important;
	font-weight: 600 !important;
	letter-spacing: 0px !important;
	marginTop:10px;
	box-shadow: none !important;
	border: 1px solid #EAEAEA !important; 
`;

export const FilledErrorButton = styled(Button)`
	color: #FFFFFF !important;
	background-color: #E33946 !important;
	border: 1px solid #E33946 !important;
	text-transform: none !important;
	&:hover {
		background-color: rgba(227, 57, 70, 0.8) !important;
		border-color: #E33946 !important;
	}
	font-weight: 600 !important;
	letter-spacing: 0px !important;
	marginTop:10px;
`;